/**
 * UnitEconomicsContext — localStorage-backed editable inputs for unit economics.
 * Provides both the raw inputs (for the admin editor) and computed outputs
 * (for the pitch deck display).
 */
import { createContext, useContext, useState, useCallback, useMemo } from "react";
import { DEFAULT_INPUTS, computeUnitEconomics } from "../data/unitEconomicsEngine";

const STORAGE_KEY = "pitch_unit_economics_inputs";

function loadInputs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULT_INPUTS, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return DEFAULT_INPUTS;
}

function saveInputs(inputs) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(inputs));
}

const UnitEconomicsContext = createContext(null);

export function UnitEconomicsProvider({ children }) {
  const [inputs, setInputsState] = useState(loadInputs);

  /** Update top-level fields (cac, totalSubs, monthlyOpex, etc.) */
  const updateGlobals = useCallback((patch) => {
    setInputsState((prev) => {
      const next = { ...prev, ...patch };
      saveInputs(next);
      return next;
    });
  }, []);

  /** Update a single tier by index */
  const updateTier = useCallback((index, patch) => {
    setInputsState((prev) => {
      const tiers = prev.tiers.map((t, i) => (i === index ? { ...t, ...patch } : t));
      const next = { ...prev, tiers };
      saveInputs(next);
      return next;
    });
  }, []);

  /** Add a new tier */
  const addTier = useCallback(() => {
    setInputsState((prev) => {
      const next = {
        ...prev,
        tiers: [...prev.tiers, { name: "New Tier", price: 100, infraCost: 30, retentionMonths: 12, mixPct: 0 }],
      };
      saveInputs(next);
      return next;
    });
  }, []);

  /** Remove a tier by index */
  const removeTier = useCallback((index) => {
    setInputsState((prev) => {
      const next = { ...prev, tiers: prev.tiers.filter((_, i) => i !== index) };
      saveInputs(next);
      return next;
    });
  }, []);

  /** Reset everything to defaults */
  const resetAll = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setInputsState(DEFAULT_INPUTS);
  }, []);

  // Computed outputs — recalculated whenever inputs change
  const computed = useMemo(() => computeUnitEconomics(inputs), [inputs]);

  return (
    <UnitEconomicsContext.Provider
      value={{ inputs, computed, updateGlobals, updateTier, addTier, removeTier, resetAll }}
    >
      {children}
    </UnitEconomicsContext.Provider>
  );
}

export function useUnitEconomics() {
  const ctx = useContext(UnitEconomicsContext);
  if (!ctx) throw new Error("useUnitEconomics must be used within UnitEconomicsProvider");
  return ctx;
}
